import type { FormEvent } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
  ChevronDown20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { AvatarUploader } from "../../components/AvatarUploader";
import {
  ApiError,
  createSupplier,
  uploadSupplierLogo,
  type SupplierPayload,
  type Supplier,
} from "../../fornecedores/fornecedoresApi";

type CategoriaFornecedor =
  | "Materiais"
  | "Serviços"
  | "Equipamentos";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para criar fornecedores.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível criar o fornecedor.";
};

export function NovoFornecedorView() {
  const navigate = useNavigate();
  const [nome, setNome] = useState("");
  const [categoria, setCategoria] = useState<
    CategoriaFornecedor | ""
  >("");
  const [nif, setNif] = useState("");
  const [pessoaContacto, setPessoaContacto] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [condicoes, setCondicoes] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(
    null,
  );

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!nome.trim()) {
      setSubmitError("O nome do fornecedor é obrigatório.");
      return;
    }

    if (!categoria) {
      setSubmitError("A categoria do fornecedor é obrigatória.");
      return;
    }

    setIsSaving(true);

    const payload: SupplierPayload = {
      name: nome.trim(),
      taxId: nif.trim() || null,
      category: categoria,
      contactName: pessoaContacto.trim() || null,
      email: email.trim() || null,
      phoneNumber: telefone.trim() || null,
      commercialTerms: condicoes.trim() || null,
    };

    let savedSupplier: Supplier;

    try {
      savedSupplier = await createSupplier(payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    if (logoFile) {
      try {
        await uploadSupplierLogo(savedSupplier.id, logoFile);
      } catch {
        setSubmitError(
          "Fornecedor criado, mas não foi possível guardar o logotipo.",
        );
        setIsSaving(false);
        return;
      }
    }

    setIsSaving(false);
    navigate("/fornecedores", {
      state: { toast: "Fornecedor criado com sucesso" },
    });
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/fornecedores")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "novo-fornecedor-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Fornecedor"}
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Novo Fornecedor"
        subtitle="Cria um novo fornecedor e define os dados de contacto."
        actions={actions}
        backButton={{ onClick: () => navigate("/fornecedores") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <form id="novo-fornecedor-form" onSubmit={handleSubmit}>
            <div className="max-w-3xl mx-auto space-y-6 mb-20">
              <div
                className="bg-card border border-border p-6 space-y-5"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h3
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Informação Principal
                </h3>

                <AvatarUploader
                  currentImage={null}
                  onImageChange={(file) => setLogoFile(file)}
                  label="Logotipo do Fornecedor"
                  subtitle="Formatos suportados: JPG, PNG. Tamanho recomendado: 200×200."
                  size="medium"
                />

                <div>
                  <label
                    htmlFor="nomeFornecedor"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Nome do Fornecedor{" "}
                    <span className="text-primary">*</span>
                  </label>
                  <input
                    id="nomeFornecedor"
                    type="text"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    placeholder="Insira o nome do fornecedor"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="categoria"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Categoria{" "}
                    <span className="text-primary">*</span>
                  </label>
                  <div className="relative">
                    <select
                      id="categoria"
                      value={categoria}
                      onChange={(e) =>
                        setCategoria(
                          e.target.value as CategoriaFornecedor,
                        )
                      }
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary appearance-none pr-10"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isSaving}
                    >
                      <option value="">
                        Selecione a categoria
                      </option>
                      <option value="Materiais">Materiais</option>
                      <option value="Serviços">Serviços</option>
                      <option value="Equipamentos">
                        Equipamentos
                      </option>
                    </select>
                    <ChevronDown20Regular className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="nif"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    NIF
                  </label>
                  <input
                    id="nif"
                    type="text"
                    value={nif}
                    onChange={(e) => setNif(e.target.value)}
                    placeholder="Número de Identificação Fiscal"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>

              </div>

              <div
                className="bg-card border border-border p-6 space-y-5"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h3
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Contacto
                </h3>

                <div>
                  <label
                    htmlFor="pessoaContacto"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Pessoa de contacto
                  </label>
                  <input
                    id="pessoaContacto"
                    type="text"
                    value={pessoaContacto}
                    onChange={(e) =>
                      setPessoaContacto(e.target.value)
                    }
                    placeholder="Nome da pessoa de contacto"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contacto@email.pt"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="telefone"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Telefone
                  </label>
                  <input
                    id="telefone"
                    type="tel"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    placeholder="+351 912 345 678"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>

                <div>
                  <label
                    htmlFor="condicoes"
                    className="block text-foreground mb-2"
                    style={{
                      fontSize: "var(--text-caption)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Condições Comerciais
                  </label>
                  <input
                    id="condicoes"
                    type="text"
                    value={condicoes}
                    onChange={(e) => setCondicoes(e.target.value)}
                    placeholder="Ex: 30 dias, 45 dias, Pronto pagamento"
                    className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    style={{
                      borderRadius: "var(--radius-button)",
                      fontSize: "var(--text-base)",
                    }}
                    disabled={isSaving}
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
